import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { abrirOverlay } from "services/overlays";
import Icon from "components/Icon";
import useDescidaDaAlca from "hooks/useDescidaDaAlca";

// Os sheets abertos, do fundo para o topo. Existe porque eles empilham: o endereço dentro do sheet
// de "Pontos de atenção" da Minha agenda abre a confirmação de mapa POR CIMA dele. Sem a pilha, as
// duas instâncias disputavam três coisas — cada uma fechava no mesmo Escape, cada uma dava add e
// remove na mesma classe de trava de rolagem (a de cima destravava o fundo saindo, com a de baixo
// ainda aberta), e quem pintava em cima era só quem tinha entrado depois no portal.
//
// Módulo, e não context: o OverlaySheet é usado solto em sete arquivos, e um Provider obrigaria
// todos eles a mudar para consertar um caso que só dois deles vivem.
const pilha = [];


// O esqueleto que OverlayCalendario e PainelProfissional já repetiam entre si, agora num lugar só —
// o agendamento precisava dele em três instâncias (calendário, confirmação, sucesso) e teria virado
// a quarta, quinta e sexta cópia. Mobile é bottom sheet, desktop é cartão centrado, e o corte é
// inteiramente CSS: renderizar duas variantes poria dois role="dialog" no documento e dois efeitos
// disputando a trava de rolagem do body.
//
// Aqueles dois arquivos continuam com a cópia local de propósito: são telas em produção e trocá-las
// aqui não muda nada para o usuário.
const OverlaySheet = ({
	aberto,
	onFechar,
	titulo,
	ariaLabel,
	larguraDesktop = "md:w-[34rem]",
	rodape,
	children,
}) => {
	const painelRef = useRef(null);
	const gatilhoRef = useRef(null);
	// A identidade desta instância dentro da `pilha`. Um objeto vazio serve: o que importa é ser
	// único e estável entre renders.
	const idRef = useRef({});
	const [profundidade, setProfundidade] = useState(0);
	const { fecharPelaAlca, estiloFundo, estiloPainel } = useDescidaDaAlca(onFechar, aberto);

	// Mantida fresca a cada render, para o botão voltar do Android chamar o fechamento de agora e
	// não o que existia quando o sheet abriu.
	const aoFecharRef = useRef(onFechar);
	aoFecharRef.current = onFechar;

	// Efeito isolado com [aberto]: junto com o listener de Escape (que depende de onFechar) ele
	// refocaria o painel a cada render do pai e roubaria o foco dos próprios controles de dentro.
	//
	// Guardar o activeElement e devolvê-lo na saída é o que OverlayCalendario não faz: lá quem
	// restaura é a página, porque ela conhece os três gatilhos possíveis. Aqui o componente é usado
	// por qualquer botão, então ele mesmo se encarrega — sem isso, fechar por Escape despeja quem
	// navega por teclado no topo do body.
	useEffect(() => {
		if (!aberto) return undefined;

		gatilhoRef.current = document.activeElement;
		painelRef.current?.focus();

		return () => {
			// O gatilho pode ter saído do DOM nesse meio-tempo. focus() em nó destacado é no-op.
			const gatilho = gatilhoRef.current;
			if (gatilho && typeof gatilho.focus === "function") gatilho.focus();
		};
	}, [aberto]);

	// Entra nas duas pilhas ao abrir, sai ao fechar.
	//
	// São duas de propósito. A daqui é só PROFUNDIDADE — quem pinta por cima de quem, entre sheets.
	// A do services/overlays é a de overlays abertos NO PROJETO INTEIRO: o painel do profissional, o
	// calendário da busca e o menu do sanduíche também cobrem a tela sem serem OverlaySheet, e é
	// dela que saem a trava de rolagem e a resposta ao botão voltar do Android.
	//
	// A ref do onFechar, e não o onFechar: o `aberto` sozinho nas dependências é o que impede este
	// efeito de rodar a cada render do pai (ver a nota do efeito de foco acima), e passar a função
	// obrigaria a incluí-la ali.
	//
	// useLayoutEffect e não useEffect: a profundidade decide o z-index, e o useEffect roda depois do
	// paint — o sheet de cima apareceria um quadro atrás do de baixo antes de subir.
	useLayoutEffect(() => {
		if (!aberto) return undefined;

		const id = idRef.current;
		pilha.push(id);
		setProfundidade(pilha.length - 1);
		const removerDaPilhaGlobal = abrirOverlay(aoFecharRef);

		return () => {
			const posicao = pilha.indexOf(id);
			if (posicao !== -1) pilha.splice(posicao, 1);
			removerDaPilhaGlobal();
		};
	}, [aberto]);

	useEffect(() => {
		if (!aberto) return undefined;
		const aoTeclar = (evento) => {
			if (evento.key !== "Escape") return;
			// Só o sheet do topo responde. Os listeners são todos do `document`, então sem esta guarda
			// um Escape fecharia a pilha inteira de uma vez — e Escape é um "não", que precisa devolver
			// a pessoa para onde ela estava, não desmontar tudo atrás dela.
			if (pilha[pilha.length - 1] !== idRef.current) return;
			onFechar();
		};
		document.addEventListener("keydown", aoTeclar);
		return () => document.removeEventListener("keydown", aoTeclar);
	}, [aberto, onFechar]);

	if (!aberto) return null;

	// Portal para o body: qualquer ancestral com backdrop-blur ou transform vira containing block
	// de position: fixed, e o overlay passaria a ser posicionado dentro do card em vez da janela.
	return createPortal(
		// z-index pela posição na pilha, e não a classe z-50 fixa de antes: com dois sheets abertos
		// quem pintava em cima era só quem tinha entrado depois no portal — funcionava por acidente
		// de ordem do DOM. O 50 continua sendo o piso, o mesmo da NavBar.
		<div className="fixed inset-0" style={{ zIndex: 50 + profundidade }}>
			<div
				onClick={onFechar}
				aria-hidden="true"
				className="absolute inset-0 bg-dark/40 transition-opacity ease-in"
				style={estiloFundo}
			/>

			{/* A descida pela alça: ver hooks/useDescidaDaAlca. */}
			<div
				ref={painelRef}
				role="dialog"
				aria-modal="true"
				aria-label={ariaLabel || titulo}
				tabIndex={-1}
				className={`absolute inset-x-0 bottom-0 flex max-h-[85dvh] flex-col rounded-t-2xl bg-surface pb-[var(--safe-bottom)] shadow-xl outline-none transition-transform ease-in md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:max-w-[calc(100vw-2rem)] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-2xl md:pb-0 ${larguraDesktop}`}
				style={estiloPainel}
			>
				{/* Alça do bottom sheet: só existe onde o painel encosta na borda de baixo. A área de
				    toque é a faixa inteira, não só o traço de 6px. Fica fora da árvore de
				    acessibilidade: o X já é o "Fechar" para leitor de tela e teclado, e um segundo
				    anunciaria a mesma ação duas vezes. */}
				<div
					onClick={fecharPelaAlca}
					aria-hidden="true"
					className="flex cursor-pointer justify-center pb-1 pt-2.5 md:hidden"
				>
					<span className="h-1.5 w-10 rounded-full bg-border" />
				</div>

				{/* Respiro do PainelProfissional: md:px-7 (28px) no lugar dos 20px de antes, com o
				    corpo em md:py-6. Num cartão de 28-34rem os 20px deixavam o conteúdo encostado
				    nas bordas. O mobile fica em px-4 — a 320px cada pixel lateral sai da largura útil
				    do texto, e é a mesma escolha que o PainelProfissional faz. */}
				<div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 md:px-7 md:py-4">
					<h2 className="t-bloco">{titulo}</h2>
					<button
						type="button"
						onClick={onFechar}
						aria-label="Fechar"
						className="btn-icone -mr-2 hover:text-primary focus:ring-primary"
					>
						{/* 28px, e não os 20px padrão do Icon: o X é a saída do sheet, e no celular
						    o glifo pequeno se perdia no canto do cabeçalho. */}
						<Icon name="close" className="h-7 w-7" />
					</button>
				</div>

				{/* min-h-0 é obrigatório: sem ele o filho flex não encolhe, o painel estoura os 85vh
				    e a rolagem vaza para o body — que está travado, então nada rola.

				    Quem sangra conteúdo até as bordas (o CabecalhoSheet da Minha agenda) espelha
				    este padding em margem negativa: mexer aqui pede o mesmo ajuste lá. */}
				<div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 md:px-7 md:py-6">{children}</div>

				{rodape && <div className="border-t border-border px-4 py-3 md:px-7 md:py-5">{rodape}</div>}
			</div>
		</div>,
		document.body
	);
};

export default OverlaySheet;
